import React, { useContext, useId, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import SolidInfoCircleIcon from 'LuxKit/Icons/solid/SolidInfoCircleIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import styled from 'styled-components'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import CFMRUnforseenEventsModal from 'components/Insurance/UnforseenEventsModal/CFMRUnforseenEventsModal'
import Clickable from 'components/Common/Clickable'
import BedbankCancellationPolicyModal from './BedbankCancellationPolicyModal'
import * as Analytics from 'analytics/analytics'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

interface Props {
  isLoading: boolean
  errorOnQuote: boolean
  quote?: App.InsuranceQuote
  onChange?: (value: 'non-refundable' | 'full-refund') => void
  refundTitle?: string
  refundDetails?: string
  defaultSelected?: 'non-refundable' | 'full-refund'
}

function BedbankRoomOptionRateInsurance({ onChange, isLoading, errorOnQuote, quote, refundTitle, refundDetails, defaultSelected = 'non-refundable' }: Props) {
  const showModal = useContext(ModalContext)
  const radioGroupName = useId()
  const [impressionRef, impressionInView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (impressionInView) {
      Analytics.trackClientEvent({
        subject: 'flex_cfmr_package',
        action: 'impression',
        type: 'operational',
        category: 'insurance',
      })
    }
  }, [impressionInView, isLoading, errorOnQuote, quote])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'non-refundable' | 'full-refund'

    if (value === 'full-refund') {
      Analytics.trackClientEvent({
        subject: 'flex_cfmr_package',
        action: 'select_cfmr_option',
        type: 'interaction',
        category: 'insurance',
      })
    }

    onChange?.(value)
  }

  const handleRefundInfoIconClick = (event: React.MouseEvent) => {
    // Prevent default behavior and stop event bubbling to avoid triggering the radio input selection
    event.preventDefault()
    event.stopPropagation()
    showModal(<CFMRUnforseenEventsModal quote={quote} />)
  }

  const handleNonRefundableInfoIconClick = () => {
    showModal(
      <BedbankCancellationPolicyModal
        heading="Non-refundable and no date changes permitted"
        policyDescriptions={['If you cancel your booking, you are not eligible for a refund or credit for a future stay.']}
      />,
    )
  }

  if (isLoading) {
    return <LoadingBox height={80} width={230} />
  }

  if (errorOnQuote || !quote?.total) return null

  return (
    <div ref={impressionRef}>
      <RadioGroup>
        <VerticalSpacer gap={12}>
          <Group direction="horizontal" gap={4}>
            <RadioInput
              name={radioGroupName}
              value="non-refundable"
              onChange={handleChange}
              defaultChecked={defaultSelected === 'non-refundable'}
            >
              Non-refundable
            </RadioInput>
            <Clickable onClick={handleNonRefundableInfoIconClick}>
              <SolidInfoCircleIcon size="S" />
            </Clickable>
          </Group>
          <StyledRadioInput
            name={radioGroupName}
            value="full-refund"
            onChange={handleChange}
            defaultChecked={defaultSelected === 'full-refund'}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
              <BodyTextBlock variant="medium" weight="bold" colour="highlight-secondary" wrap="no-wrap">
                {refundTitle}
              </BodyTextBlock>
              <BodyTextBlock variant="medium" weight="bold" colour="neutral-two" wrap="no-wrap">
                +<FormatCurrency value={quote?.total} />
              </BodyTextBlock>
              <BodyTextBlock variant="medium" colour="neutral-two" weight="normal" wrap="no-wrap">
                {refundDetails}
              </BodyTextBlock>
              <Clickable as="span" onClick={handleRefundInfoIconClick}>
                <IconContainer>
                  <SolidInfoCircleIcon colour="neutral-two" />
                </IconContainer>
              </Clickable>
            </Group>
          </StyledRadioInput>
        </VerticalSpacer>
      </RadioGroup>
    </div>
  )
}

export default BedbankRoomOptionRateInsurance
